import { BASE_PATH } from '@trinnvis/dabih-api-client';
import type { State } from 'src/store/types.js';
import { LocalDate } from 'src/utilities/local-date.js';
import { key } from 'src/store/selectors/selectors';
import type { ApiClient } from 'src/store/backend';
import { getClassificationText } from 'src/store/selectors';
import { getPromoDataForEntity } from 'src/store/selectors/promo';
import { currentUserUuid } from '../../store/selectors/user/currentUserUuid';
import { writeAccess } from '../../store/selectors/user/writeAccess';
import { toDocumentViewModel } from '../../store/selectors/documents/to-document-view-model';
import { getOrganization } from '../../store/selectors/organization/getOrganization';
import type { DocumentViewEditItem } from '../../content/documents/document-view-edit-item';
import type { DocumentView } from '../../content/documents/document-view';
import type { CommonDataEntityView } from '../../content/common-data-entity-view';
import { assertIsDefined } from '../../assertIsDefined';
import { toAttachmentItems } from '../to-attachment-items';
import { fetchRevisions } from '../fetch-revisions';
import { pagesAsSelectTagOptions } from '../pages-as-select-tag-options';
import { fetchDraft } from '../fetch-draft';
import { getFeatureStates } from '../../store/selectors/features/getFeatureStates';

export async function buildDocumentView(
  api: ApiClient,
  uuid: string,
  commonData: CommonDataEntityView,
  state: State,
  currentParent: string,
): Promise<DocumentView> {
  const organization = getOrganization(state);
  assertIsDefined(organization, 'organization for buildDocumentView');

  const availablePages = pagesAsSelectTagOptions(state);

  const item = organization.documentsById[uuid];
  assertIsDefined(item, `document ${uuid} in organization.documentsById`);

  const entity = toDocumentViewModel(item);

  return {
    ...commonData,
    promoData: getPromoDataForEntity('documents', item, getFeatureStates(state)),
    docsForLinking: [],
    contentLastModified: entity.contentLastModified ?? '',
    contentLastModifiedBy: entity.contentLastModifiedBy ?? '',
    contentLastModifiedWasCreate: entity.contentLastModifiedWasCreate ?? false,
    hasTemplateDelete: entity.hasTemplateDelete ?? false,
    hasTemplateUpdate: entity.hasTemplateUpdate ?? false,
    templateDeclined: entity.templateDeclined ?? '',
    templateDeleted: entity.templateDeleted ?? '',
    templateDeletedMessage: entity.templateDeletedMessage ?? '',
    templateUpdated: entity.templateUpdated ?? '',
    currentUserHasAccess:
      entity.classification === 'NONE' || (entity.accessControl ?? []).includes(currentUserUuid(state) ?? ''),
    currentUserHasWriteAccess: writeAccess(state),

    uuid: uuid,
    isConfirmedEntity: entity.isConfirmedEntity,
    helpContent: entity.helpContent ?? '',
    href: currentParent + '/documents/' + uuid,
    pdfHref:
      BASE_PATH + '/organizations/' + organization.organizationId + '/documents/' + uuid + '.pdf?key=' + key(state),
    parentHref: currentParent,
    deleted: entity.deleted === true,
    type: 'documents',
    name: entity.name ?? '',
    classification: entity.classification ?? 'NONE',
    accessControl: entity.accessControl ?? [],
    classificationText: getClassificationText(
      entity.classification ?? 'NONE',
      entity.accessControl ?? [],
      commonData.accessControlOptions,
    ),
    content: entity.content ?? '',
    revisions: await fetchRevisions(
      api,
      'documents',
      uuid,
      organization.organizationId,
      entity.templateDeleted !== undefined && entity.templateDeleted !== '',
      undefined,
      entity.contentLastModified,
    ),
    attachments: toAttachmentItems(api, 'documents', entity.uuid, entity.attachments),
    validFromDate: entity.validFromDate ?? '',
    validToDate: entity.validToDate ?? '',
    availablePages: availablePages,
    pages: (entity.pages ?? []).map((x) => '' + x),
    today: LocalDate.fromString(state.today),
    deletable: true,
    hasDraft: entity.hasDraft,
    fetchDraft: async () => {
      const command = await fetchDraft(api, 'documents', uuid, organization.organizationId);
      return command.draft as DocumentViewEditItem;
    },
  };
}
